/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import {
  BuilderFormSchemaFactory,
  FormRootSchema,
  FormStateSchema,
  PrinterFinderListItem,
} from '@gh/builder-shared';
import { EomReportMethod, EomReportsHistory } from '@gh/builder-shared-data';
import { FormState, NumberTypes } from '@gh/common-shared';
import { ID, isProduceStatusCompleted, ProduceStatus } from '@gh/common-shared-data';
import { controlValues, FormMode, getControl, getControlValue, setControlValue } from '@gh/core-ui';
import { Observable } from 'rxjs';
import { EomReport } from '../../resources';
import { isNil } from 'lodash';

@Injectable()
export class EomReportDetailsFormStateService extends FormState<EomReport> {
  produceStatus$: Observable<ProduceStatus>;
  isCloud: boolean;

  constructor(schemaFactory: BuilderFormSchemaFactory) {
    super(schemaFactory);
  }

  get produceStatus(): ProduceStatus {
    return getControlValue(this.group, 'produceStatus');
  }

  get isAuthCodeRequired(): boolean {
    return getControlValue(this.group, 'method') === EomReportMethod.PrintedOnSelectedPrinter;
  }

  get printerId(): Maybe<ID> {
    return getControlValue(this.group, 'printerId');
  }

  build(report: EomReport): void {
    this.initSchema(report);

    this.produceStatus$ = controlValues(getControl(this.group, 'produceStatus'));
  }

  setProduceStatus(produceStatus: ProduceStatus): void {
    setControlValue(this.group, 'produceStatus', produceStatus);
  }

  isPrintedMethod(method: EomReportMethod): boolean {
    return (method === EomReportMethod.PrintedOnSelectedPrinter && this.isCloud);
  }

  updateHistory(item: EomReportsHistory): void {
    setControlValue(this.group, 'method', EomReportMethod.PrintedOnSelectedPrinter);
    setControlValue(this.group, 'forMonth', item.forMonth);
    setControlValue(this.group, 'forYear', item.forYear);
    setControlValue(this.group, 'memo', item.memo);
    setControlValue(this.group, 'noOfStaff', isNil(item.noOfStaff) ? 0 : item.noOfStaff);
    setControlValue(this.group, 'noOfLeads', isNil(item.noOfLeads) ? 0 : item.noOfLeads);
    setControlValue(this.group, 'noOfSales', isNil(item.noOfSales) ? 0 : item.noOfSales);
    setControlValue(this.group, 'staffHoursPaid', isNil(item.staffHoursPaid) ? 0 : item.staffHoursPaid);
    setControlValue(this.group, 'trafficValue', isNil(item.traffic) ? 0 : item.traffic);
    setControlValue(this.group, 'contractDateJobsCount', isNil(item.contractDateJobsCount) ? 0 : item.contractDateJobsCount);
    setControlValue(this.group, 'finalizedJobsCount', isNil(item.finalizedJobsCount) ? 0 : item.finalizedJobsCount);
    setControlValue(this.group, 'notProceedingDppJobsCount', isNil(item.notProceedingDppJobsCount) ? 0 : item.notProceedingDppJobsCount);
    setControlValue(this.group, 'version', item.version);
    setControlValue(this.group, 'whs', item.whs);
    setControlValue(this.group, 'whsDetails', item.whsDetails);
  }

  uncheckAllCheckboxes(): void {
    setControlValue(this.group, 'reconciledBanksAccounts', false);
    setControlValue(this.group, 'enteredJobExpenses', false);
    setControlValue(this.group, 'finalizedJobsConfirmed', false);
    setControlValue(this.group, 'lockedDatesInXero', false);
    setControlValue(this.group, 'notProceedingDpp', false);
    setControlValue(this.group, 'contractDates', false);
    setControlValue(this.group, 'checkedSalesManagement', false);
    setControlValue(this.group, 'checkedWorkInProgress', false);    
    setControlValue(this.group, 'countedTraffic', false);
    setControlValue(this.group, 'advisedStaffFigures', false);
  }

  updateMethod(isCloud: boolean): void {
    this.isCloud = isCloud;
    this.updatePrinter();
    this.refresh();
  }

  updatePrinter(printer?: PrinterFinderListItem): void {
    setControlValue(this.group, 'printerId', printer && printer.cloudPrinterId);
    setControlValue(this.group, 'printer', printer && printer.cloudPrinterName);
  }

  getForMonthControlValue(): number {
    return getControlValue(this.group, 'forMonth');
  }

  getForYearControlValue(): number {
    return getControlValue(this.group, 'forYear');
  }

  getWhs(): boolean {
    return getControlValue(this.group, 'whs');
  }

  getWhsDetails(): string {
    return getControlValue(this.group, 'whsDetails');
  }

  protected buildSchema(report: EomReport, { root }: FormStateSchema): FormRootSchema {
    return root({
      enabled: isProduceStatusCompleted(report.produceStatus),
      mode: FormMode.EditMode,
      optimisticLock: true,
      children: {
        id: void 0,
        forMonth: void 0,
        forYear: void 0,
        method: void 0,
        printerId: void 0,
        authCode: void 0,
        reconciledBanksAccounts: void 0,
        enteredJobExpenses: void 0,
        finalizedJobsConfirmed: void 0,
        finalizedJobsCount: void 0,
        notProceedingDpp: void 0,
        notProceedingDppJobsCount: void 0,
        contractDates: void 0,
        contractDateJobsCount: void 0,
        lockedDatesInXero: void 0,
        checkedSalesManagement: void 0,
        checkedWorkInProgress: void 0,
        noOfLeads: void 0,
        noOfSales: void 0,
        countedTraffic: void 0,
        trafficValue: { type: NumberTypes.Quantity },
        advisedStaffFigures: void 0,
        noOfStaff: { type: NumberTypes.Quantity },
        staffHoursPaid: { type: NumberTypes.Quantity },
        memo: void 0,
        monthlyDeposit: void 0,
        monthlySlabs: void 0,
        monthlyContracts: void 0,
        jobProfitFinalized: void 0,
        profitOfFinalizedJobs: void 0,
        salesProfitForecast: void 0,
        franchiseMonthly: void 0,
        wip: void 0,
        profitAndLoss: void 0,
        balanceSheet: void 0,
        executiveSummary: void 0,
        salesManagement: void 0,
        buildersInsurance: void 0,
        version: void 0,
        whs: void 0,
        whsDetails: void 0,
        printer: {
          required: this.isPrintedMethod(report.method),
          visible: this.isPrintedMethod(report.method),
        },
        produceStatus: void 0,
        historyEomSettings: void 0,
      },
    });
  }
}
